import { useState, useCallback, useEffect } from "react";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";

// components
import ContentTile from "./ContentTile";
import RecentContentSkeleton from "./RecentContentSkeleton";
// hooks
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
// redux
import { useDispatch } from "../../../redux/store";
import { setSelectedContent } from "../../../redux/slices/creator";
import { useLazyGetRecentIGPostsForCreatorQuery } from "../../../redux/apiSlices/discover";
// config
import colors from "../../../config/colors";
// ga
import {
  triggerGa,
  CATEGORY,
  ACTION,
  PAGE,
  SCREEN,
  LABEL,
  LABEL2,
} from "../../../utils/gaUtils";

const RecentContent = ({ wishlink_username, instagram_username = "" }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [content, setContent] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const dispatch = useDispatch();
  const [fetchRecentIGPosts, { isFetching }] =
    useLazyGetRecentIGPostsForCreatorQuery();

  const hasMore = pagination?.has_next || false;

  const getInsightValue = (insights, name) => {
    const rows = insights?.data || [];
    const row = rows.find((x) => x?.name === name);
    const v = row?.values?.[0]?.value;
    return typeof v === "number" ? v : Number(v) || 0;
  };

  const normalizeIgPost = (p) => {
    const insights = p?.insights;

    return {
      post_id: p?.id,
      post_type: p?.media_type,
      post_created: p?.timestamp,

      thumbnail_url: p?.thumbnail_url,
      media_url: p?.media_url,
      permalink: p?.permalink,
      caption: p?.caption,

      total_views: getInsightValue(insights, "views"),
      total_reach: getInsightValue(insights, "reach"),
      total_likes:
        getInsightValue(insights, "likes") || Number(p?.like_count) || 0,
      total_comments: getInsightValue(insights, "comments"),
      total_shares: getInsightValue(insights, "shares"),
    };
  };

  const loadRecentContentData = useCallback(async () => {
    if (!wishlink_username) return;

    try {
      const result = await fetchRecentIGPosts({ wishlink_username }).unwrap();

      // endpoint returns array of posts
      const posts = Array.isArray(result) ? result : result?.data || [];
      setContent(posts.map(normalizeIgPost));

      setIsInitialLoading(false);
    } catch (error) {
      console.error("Error loading recent IG posts:", error);
      setIsInitialLoading(false);
    }
  }, [wishlink_username, fetchRecentIGPosts]);

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadRecentContentData(nextPage);
    }
  }, [hasMore, isFetching, currentPage, loadRecentContentData]);

  const containerRef = useInfiniteScroll(loadMore, hasMore, isFetching, 100);

  const handleClickContent = (item) => {
    dispatch(setSelectedContent(null));
    setTimeout(() => {
      triggerGa({
        category: CATEGORY.USER_INTERACTION,
        action: ACTION.RECENT_CONTENT,
        label: instagram_username,
        label2: item?.permalink,
        label3: LABEL2.PROFILE_INTEREST,
        page: PAGE.CREATOR_DISCOVER,
        screenName: SCREEN.CREATOR_DRAWER,
      });
      dispatch(setSelectedContent(item));
    }, 100);
  };
  useEffect(() => {
    if (wishlink_username) {
      setCurrentPage(1);
      setContent([]);
      setPagination(null);
      setIsInitialLoading(true);
      loadRecentContentData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishlink_username]);

  if (isInitialLoading) {
    return <RecentContentSkeleton label="Recent Content" />;
  }

  if (content?.length === 0) {
    return (
      <Box>
        <Typography fontSize={12} mb={1}>
          Recent Content:
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "139px",
          }}
        >
          <Typography
            variant="body2"
            color={colors.gray200}
            sx={{ textAlign: "center" }}
          >
            No recent content available.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ gap: "8px" }}>
      <Typography fontSize={12} fontWeight={500} lineHeight="20px" mb={1}>
        Recent Content :
      </Typography>
      <Box
        ref={containerRef}
        sx={{
          overflow: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "2px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: colors.gray100,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.primary500,
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: colors.primary500,
            opacity: 0.8,
          },
        }}
      >
        <Stack
          direction="row"
          gap={1}
          sx={{
            flexWrap: "wrap",
            alignContent: "flex-start",
          }}
        >
          {content?.map((item) => (
            <ContentTile
              key={item?.post_id}
              item={item}
              onClick={() => handleClickContent(item)}
            />
          ))}
        </Stack>
        {isFetching && !isInitialLoading && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={20} />
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default RecentContent;
